/** Horizontal distance between adjacent timeline node centers (slot + connector). */
export const TIMELINE_NODE_STRIDE_PX = 104;

/**
 * Whether an element is fully visible within a horizontal scroll container.
 */
export function isElementInHorizontalScrollViewport(
  container: HTMLElement,
  element: HTMLElement
): boolean {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  return (
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right
  );
}

/**
 * Scroll a timeline node into view, centering it horizontally when it is
 * outside the visible scrollport.
 */
export function scrollTimelineNodeIntoView(
  container: HTMLElement,
  element: HTMLElement
): void {
  if (isElementInHorizontalScrollViewport(container, element)) {
    return;
  }
  scrollTimelineNodeToCenter(container, element, "smooth");
}

/** How to align the active node when auto-scrolling the timeline strip. */
export type TimelineNodeScrollAlignment = "center" | "start" | "end";

/** Target scrollLeft for placing a node per alignment (no scroll performed). */
export function getTimelineNodeTargetScrollLeft(
  container: HTMLElement,
  element: HTMLElement,
  alignment: TimelineNodeScrollAlignment = "center"
): number {
  const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

  if (alignment === "start") {
    return 0;
  }
  if (alignment === "end") {
    return maxScrollLeft;
  }

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const nodeLeftInContent =
    container.scrollLeft + (elementRect.left - containerRect.left);
  const nodeRightInContent = nodeLeftInContent + elementRect.width;
  const nodeCenterInContent = nodeLeftInContent + elementRect.width / 2;

  let targetScrollLeft = nodeCenterInContent - container.clientWidth / 2;
  targetScrollLeft = Math.max(
    targetScrollLeft,
    nodeRightInContent - container.clientWidth
  );
  targetScrollLeft = Math.min(targetScrollLeft, nodeLeftInContent);
  return Math.min(maxScrollLeft, Math.max(0, targetScrollLeft));
}

/**
 * Center a timeline node in its scroll container using scrollLeft math so
 * positioning is synchronous (not interrupted by remounts).
 */
export function scrollTimelineNodeToCenter(
  container: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior = "smooth",
  alignment: TimelineNodeScrollAlignment = "center"
): void {
  const targetScrollLeft = getTimelineNodeTargetScrollLeft(
    container,
    element,
    alignment
  );

  container.scrollTo({ left: targetScrollLeft, behavior });
}

/** Center a timeline node horizontally (smooth scroll for user actions). */
export function centerTimelineNodeInView(
  container: HTMLElement,
  element: HTMLElement,
  alignment: TimelineNodeScrollAlignment = "center"
): void {
  scrollTimelineNodeToCenter(container, element, "smooth", alignment);
}

/** Whether the scroll container has overflow on the left or right edge. */
export function getHorizontalScrollOverflow(
  container: HTMLElement
): { canScrollLeft: boolean; canScrollRight: boolean } {
  const { scrollLeft, scrollWidth, clientWidth } = container;
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
  return {
    canScrollLeft: scrollLeft > 1,
    canScrollRight: scrollLeft < maxScrollLeft - 1,
  };
}

/** Scroll the timeline strip horizontally by one node step. */
export function scrollTimelineByOneStep(
  container: HTMLElement,
  direction: "left" | "right"
): void {
  const delta =
    direction === "left" ? -TIMELINE_NODE_STRIDE_PX : TIMELINE_NODE_STRIDE_PX;
  container.scrollBy({ left: delta, behavior: "smooth" });
}
